import { dimensionMetricsPages } from '@/components/PDFDownloadBtn/PDFBox/PDFBox'
import { genRangeDesc } from '@/pages/ProjectDetailPage/components/StoryboardAnalysis/StoryboardAnalysis'
import PptPageMomentCover from '../PptPageMomentCover/PptPageMomentCover'
import PptPageMomentResults from '../PptPageMomentResults/PptPageMomentResults'
import { SliderModels } from '@/types/slider'
import PptPageMomentAdvantage from '../PptPageMomentAdvantage/PptPageMomentAdvantage'
import PptPageMomentSuggestions from '../PptPageMomentSuggestions/PptPageMomentSuggestions'

interface Period {
  momentKey: keyof ProjectModels.ProjectDetail['dimensionMetrics']
  score: number
  rangeDesc: string
  ranges: Range[]
  baseNo: number
}

interface Range {
  advantage: string
  disadvantage: string
  suggestions: string
}

const getRangeCnt = (range: Range) => {
  let cnt = 0
  const first = 0
  let second = 0
  let third = 0
  if (range.advantage && range.disadvantage) {
    cnt += 1
    second = cnt
  }
  if (range.suggestions) {
    cnt += 1
    third = cnt
  }
  return { first, second, third, cnt }
}

function PeriodPptPage(props: {
  project: ProjectModels.ProjectDetail
  baseNo: number
  onSliderReady: (slider: SliderModels.Slider, no: number) => void
}) {
  const firstThreeDimension = props.project.dimensionMetrics.firstThree
  const keyInformationDimension = props.project.dimensionMetrics.keyInformation
  const brandConnectionDimension = props.project.dimensionMetrics.brandConnection
  const interestingEnagementDimension = props.project.dimensionMetrics.interestingEnagement
  const lastThreeDimension = props.project.dimensionMetrics.lastThree

  /** 不要修改这里顺序，如果修改，则对应的 baseNo 的计算顺序也要修改 */
  const periods: Period[] = [
    {
      momentKey: 'firstThree' as keyof ProjectModels.ProjectDetail['dimensionMetrics'],
      score: firstThreeDimension.score,
      rangeDesc: genRangeDesc(firstThreeDimension),
      ranges: [
        {
          advantage: firstThreeDimension.reason.advantage || '',
          disadvantage: firstThreeDimension.reason.disadvantage || '',
          suggestions: firstThreeDimension.reason.suggestions || '',
        },
      ],
      baseNo: props.baseNo,
    },
    {
      momentKey: 'keyInformation' as keyof ProjectModels.ProjectDetail['dimensionMetrics'],
      score: keyInformationDimension.overall.score,
      rangeDesc: keyInformationDimension.periods.map((item) => genRangeDesc(item)).join('、'),
      ranges: [
        {
          advantage: keyInformationDimension.overall.reason.advantage || '',
          disadvantage: keyInformationDimension.overall.reason.disadvantage || '',
          suggestions: keyInformationDimension.overall.reason.suggestions || '',
        }
      ],
      baseNo: props.baseNo + dimensionMetricsPages('firstThree', props.project),
    },
    {
      momentKey: 'brandConnection' as keyof ProjectModels.ProjectDetail['dimensionMetrics'],
      score: brandConnectionDimension.overall.score,
      rangeDesc: brandConnectionDimension.periods.map((item) => genRangeDesc(item)).join('、'),
      ranges: [
        {
          advantage: brandConnectionDimension.overall.reason.advantage || '',
          disadvantage: brandConnectionDimension.overall.reason.disadvantage || '',
          suggestions: brandConnectionDimension.overall.reason.suggestions || '',
        }
      ],
      baseNo:
        props.baseNo +
        dimensionMetricsPages('firstThree', props.project) +
        dimensionMetricsPages('keyInformation', props.project),
    },
    {
      momentKey: 'interestingEnagement' as keyof ProjectModels.ProjectDetail['dimensionMetrics'],
      score: interestingEnagementDimension.overall.score,
      rangeDesc: interestingEnagementDimension.periods.map((item) => genRangeDesc(item)).join('、'),
      ranges: [
        {
          advantage: interestingEnagementDimension.overall.reason.advantage || '',
          disadvantage: interestingEnagementDimension.overall.reason.disadvantage || '',
          suggestions: interestingEnagementDimension.overall.reason.suggestions || '',
        }
      ],
      baseNo:
        props.baseNo +
        dimensionMetricsPages('firstThree', props.project) +
        dimensionMetricsPages('keyInformation', props.project) +
        dimensionMetricsPages('brandConnection', props.project),
    },
    {
      momentKey: 'lastThree' as keyof ProjectModels.ProjectDetail['dimensionMetrics'],
      score: lastThreeDimension.score,
      rangeDesc: genRangeDesc(lastThreeDimension),
      ranges: [
        {
          advantage: lastThreeDimension.reason.advantage || '',
          disadvantage: lastThreeDimension.reason.disadvantage || '',
          suggestions: lastThreeDimension.reason.suggestions || '',
        },
      ],
      baseNo:
        props.baseNo +
        dimensionMetricsPages('firstThree', props.project) +
        dimensionMetricsPages('keyInformation', props.project) +
        dimensionMetricsPages('brandConnection', props.project) +
        dimensionMetricsPages('interestingEnagement', props.project),
    },
  ].filter((item) => item.score > 0)

  const periodsCnt = periods.map((period) => {
    const rangeCnt = period.ranges.reduce((prev: number, cur) => {
      const { cnt } = getRangeCnt(cur)
      return prev + cnt
    }, 0)
    return rangeCnt + 1
  })
  const periodsBaseNo = [0].concat(periodsCnt.slice(0, -1)).reduce((prev: number[], cur) => {
    if (prev.length === 0) {
      return [cur]
    }
    const prevCnt = prev[prev.length - 1]
    return [...prev, prevCnt + cur]
  }, [])

  return (
    <>
      <PptPageMomentCover
        no={props.baseNo + 1}
        project={props.project}
        onSliderReady={props.onSliderReady}
      />
      {periods.map((period, index) => {
        return (
          <PptMoment
            baseNo={props.baseNo + 1 + periodsBaseNo[index]}
            key={period.momentKey}
            period={period}
            project={props.project}
            onSliderReady={props.onSliderReady}
          />
        )
      })}
    </>
  )
}

function PptMoment(props: {
  period: Period
  project: ProjectModels.ProjectDetail
  onSliderReady: (slider: SliderModels.Slider, no: number) => void
  baseNo: number
}) {
  const { period, project, onSliderReady, baseNo } = props


  return (
    <>
      <PptPageMomentResults
        no={baseNo + 1}
        momentKey={period.momentKey}
        project={project}
        onSliderReady={onSliderReady}
      />
      <PptRange
        baseNo={baseNo + 1}
        range={period.ranges[0]}
        project={project}
        onSliderReady={onSliderReady}
      />

    </>
  )
}

function PptRange(props: {
  range: Range
  onSliderReady: (slider: SliderModels.Slider, no: number) => void
  project: ProjectModels.ProjectDetail
  baseNo: number
}) {
  const { range, onSliderReady } = props
  const cnt = getRangeCnt(range)

  return (
    <>
      {range.advantage && (
        <PptPageMomentAdvantage
          no={props.baseNo + cnt.second}
          project={props.project}
          advantage={range.advantage}
          disadvantage={range.disadvantage}
          onSliderReady={onSliderReady}
        />
      )}
      {range.suggestions && (
        <PptPageMomentSuggestions
          no={props.baseNo + cnt.third}
          project={props.project}
          suggestions={range.suggestions}
          onSliderReady={onSliderReady}
        />
      )}
    </>
  )
}

export default PeriodPptPage
